import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { groupsManagement } from "../../hooks/api/GroupsManagement";
import { DefaultLayout } from "../../layouts/Default";
import { TopNavigation } from "../../themes/Default/components/TopNavigation";
import { Button, ButtonBack } from "../../themes/Default/components/TopNavigation/components/Button";
import { Slide, toast, ToastContainer } from "react-toastify";
import { Tabs } from "../../themes/Default/components/Tabs";
import { Main } from "../../themes/Default/components/Main";
import { Title } from "../../themes/Default/components/Title";
import { Breadcrumps } from "../../themes/Default/components/Breadcrumps";
import { Link } from "../../themes/Default/components/Breadcrumps/components/Link";
import { Formik } from "formik";
import { Form } from "../../themes/Default/components/Form";
import { Row } from "../../themes/Default/components/Form/components/Row";
import { Field } from "../../themes/Default/components/Form/components/Row/components/Field";
import { Textarea } from "../../themes/Default/components/Form/components/Textarea";
import { Toaster } from "../../themes/Default/components/Toaster";

const ArchiveNote = () => {
    const { t } = useTranslation("common");
    const { groupId } = useParams();
    const formRef = useRef(null);
    const navigate = useNavigate();
    const [formErrors, setFormErrors] = useState([]);
    const [groupName, setGroupName] = useState("");
    const { useViewGroupMutation, useArchiveGroupMutation } = groupsManagement;
    const [viewGroup] = useViewGroupMutation();
    const [isStarted, setIsStarted] = useState([]);
    const [hasTests, setHasTests] = useState(false);
    const [hasProgress, setHasProgress] = useState(false);
    const [hasReport, setHasReport] = useState(false);
    const [hasPlan, setHasPlan] = useState(false);
    const [canArchiveGroup] = useState(true);

    const [groupArchive] = useArchiveGroupMutation();
    
    const tabs = [
        ...(isStarted ? [
            { title: t('groups.attendance'), onClick: () => gotoAttendanceTab(groupId)  },
            ...(hasPlan ? [
                { title: t('groups.planning'), onClick: () => gotoPlanningTab(groupId)},
            ] : []), 
            ...(hasProgress ? [
                { title: t('groups.progress'), onClick: () => gotoProgressTab(groupId)},
            ] : []), 
            ...(hasTests ? [
                { title: t('groups.progressTest'), onClick: () => gotoProgressTestTab(groupId) }
            ] : []), 
            ...(hasReport ? [
                { title: t('reports.title'),onClick: () => gotoReportTab(groupId)},
            ] : []), 
            { title: t('documents'), onClick: () => gotoDocumentsTab(groupId)},
            { title: t('notes'), onClick: () => gotoNotesTab(groupId)},
        ] : []),
        { title: t("settings.title"), link: "/settings/reports", isActive: true },
    ];

    const gotoPlanningTab = (groupId) => {
        navigate("/groups/" + groupId + "/planning");
    }

    const gotoReportTab = (groupId) => {
        navigate("/groups/" + groupId + "/reports");
    }

    const gotoProgressTestTab = (groupId) => {
        navigate("/groups/" + groupId + "/tests");
    }

    const gotoProgressTab = (groupId) => {
        navigate("/groups/" + groupId + "/progress");
    }

    const gotoNotesTab = (groupId) => {
        navigate("/groups/" + groupId + "/notes");
    }

    const gotoDocumentsTab = (groupId) => {
        navigate("/groups/" + groupId + "/documents");
    }

    const gotoAttendanceTab = (groupId) => {
        navigate("/groups/" + groupId + "/attendance");
    }

    const onSave = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onArchive = (values) => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("groups.confirmArchive")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        const body = {
                            ...values,
                        }; 
                        groupArchive({ groupId, body }).then((result) => {
                            if (result?.error) {
                                setFormErrors(result?.error?.data?.error?.violations);
                                toast.error(result.error.data.error.responseMessage);
                            } else {
                                navigate("/groups/edit/" + groupId);
                            }
                        });
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    useEffect(() => {
        viewGroup(groupId).then((result) => {
            setGroupName(result?.data?.name);
            setIsStarted(result?.data?.hasStarted);
            setHasTests(result?.data?.hasTests);
            setHasReport(result?.data?.hasReport);
            setHasProgress(result?.data?.hasProgress);
            setHasPlan(result?.data?.hasPlan);
        });
    }, []);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to={"/groups/edit/" + groupId} />
                <Button title={t("groups.archiveGroup")} icon="fa-regular fa-box-archive" onClick={onSave} isDisabled={!canArchiveGroup}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{groupName}</Title>
                <Breadcrumps>
                    <Link title={t("groups.title")} icon="fa-light fa-users" />
                    <Link title={t("details")} isActive={true} />
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        note: "",
                    }}
                    onSubmit={onArchive}
                >
                    {({ values, handleChange, setFieldValue }) => (
                        <Form>
                            <Row>
                                <Field isFullWidth={true}>
                                    <Textarea
                                        rows={6}
                                        name="note"
                                        label={t("groups.reasonForArchiving")}
                                        required={true}
                                        value={values.note}
                                        onChange={handleChange}
                                        error={formErrors?.note}
                                    />
                                </Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default ArchiveNote;